import type { MapView } from '../map/model';
import type { CompositionCamera } from './model';
import { constrainedWorldView } from '../map/worldViewport';

/** MapLibre's canonical 512-pixel world and spherical Web Mercator assumptions. */
export const WEB_MERCATOR_EARTH_RADIUS_METRES = 6_378_137;
export const WEB_MERCATOR_EARTH_CIRCUMFERENCE_METRES =
  2 * Math.PI * WEB_MERCATOR_EARTH_RADIUS_METRES;
export const WEB_MERCATOR_MAX_LATITUDE = 85.0511287798066;
export const MAPLIBRE_WORLD_TILE_SIZE_PX = 512;

export const COMPOSITION_SCALE_DENOMINATOR_MIN = 100;
export const COMPOSITION_SCALE_DENOMINATOR_MAX = 1_000_000_000;
export const COMPOSITION_EXACT_SCALE_PITCH_EPSILON = 1e-6;

/** Canonical CSS density used for renderer logical pixels; it is never persisted. */
export const COMPOSITION_LOGICAL_DPI = 96;
export const COMPOSITION_LOGICAL_PIXELS_PER_MM = COMPOSITION_LOGICAL_DPI / 25.4;
/** Alias retained to make the pre-5C migration authority explicit. */
export const LEGACY_COMPOSITION_PIXELS_PER_MM = COMPOSITION_LOGICAL_PIXELS_PER_MM;

/** Scale logical annotation pixels into the current physical page preview density. */
export function compositionAnnotationRenderScale(pixelsPerMm: number): number {
  return Number.isFinite(pixelsPerMm) && pixelsPerMm > 0
    ? pixelsPerMm / COMPOSITION_LOGICAL_PIXELS_PER_MM
    : 1;
}

/** MapLibre layout viewport at a supplied Composer logical density. */
export function compositionLogicalViewport(
  widthMm: number,
  heightMm: number,
  logicalPixelsPerMm = COMPOSITION_LOGICAL_PIXELS_PER_MM,
): {
  readonly widthPx: number;
  readonly heightPx: number;
} {
  const logicalPixels = (millimetres: number) =>
    Number.isFinite(millimetres) &&
    millimetres > 0 &&
    Number.isFinite(logicalPixelsPerMm) &&
    logicalPixelsPerMm > 0
      ? Math.max(1, Math.round(millimetres * logicalPixelsPerMm))
      : 1;
  return {
    widthPx: logicalPixels(widthMm),
    heightPx: logicalPixels(heightMm),
  };
}

function isPositiveFinite(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

export function clampWebMercatorLatitude(latitude: number): number {
  if (!Number.isFinite(latitude)) return 0;
  return Math.max(-WEB_MERCATOR_MAX_LATITUDE, Math.min(WEB_MERCATOR_MAX_LATITUDE, latitude));
}

export function isScaleDenominatorSupported(value: number): boolean {
  return (
    Number.isFinite(value) &&
    value >= COMPOSITION_SCALE_DENOMINATOR_MIN &&
    value <= COMPOSITION_SCALE_DENOMINATOR_MAX
  );
}

/** Local ground metres represented by one render pixel at the map centre. */
export function webMercatorGroundMetresPerPixel(zoom: number, latitude: number): number | null {
  if (!Number.isFinite(zoom)) return null;
  const resolution =
    (WEB_MERCATOR_EARTH_CIRCUMFERENCE_METRES *
      Math.cos((clampWebMercatorLatitude(latitude) * Math.PI) / 180)) /
    (MAPLIBRE_WORLD_TILE_SIZE_PX * 2 ** zoom);
  return isPositiveFinite(resolution) ? resolution : null;
}

export function zoomForScaleDenominator(
  scaleDenominator: number,
  latitude: number,
  pixelsPerMm: number,
): number | null {
  if (!isScaleDenominatorSupported(scaleDenominator) || !isPositiveFinite(pixelsPerMm)) {
    return null;
  }
  const latitudeFactor = Math.cos((clampWebMercatorLatitude(latitude) * Math.PI) / 180);
  const zoom = Math.log2(
    (WEB_MERCATOR_EARTH_CIRCUMFERENCE_METRES * latitudeFactor * 1000 * pixelsPerMm) /
      (MAPLIBRE_WORLD_TILE_SIZE_PX * scaleDenominator),
  );
  return Number.isFinite(zoom) ? zoom : null;
}

export function scaleDenominatorForZoom(
  zoom: number,
  latitude: number,
  pixelsPerMm: number,
): number | null {
  if (!isPositiveFinite(pixelsPerMm)) return null;
  const metresPerPixel = webMercatorGroundMetresPerPixel(zoom, latitude);
  if (metresPerPixel === null) return null;
  const denominator = metresPerPixel * 1000 * pixelsPerMm;
  return isPositiveFinite(denominator) ? denominator : null;
}

export function mapViewForCompositionCamera(
  camera: CompositionCamera,
  pixelsPerMm: number,
): MapView | null {
  const zoom = zoomForScaleDenominator(camera.scaleDenominator, camera.center[1], pixelsPerMm);
  return zoom === null
    ? null
    : {
        center: [...camera.center],
        zoom,
        bearing: camera.bearing,
        pitch: camera.pitch,
      };
}

export type CompositionCameraChangeKind = 'move' | 'zoom';

/** Derive displayed camera/scale for one-world framing without rewriting saved properties. */
export function effectiveCompositionCamera(
  camera: CompositionCamera,
  widthMm: number,
  heightMm: number,
  pixelsPerMm: number,
): CompositionCamera {
  const view = mapViewForCompositionCamera(camera, pixelsPerMm);
  if (!view) return camera;
  const viewport = compositionLogicalViewport(widthMm, heightMm, pixelsPerMm);
  const effective = constrainedWorldView(view, viewport.widthPx, viewport.heightPx);
  if (
    Math.abs(effective.zoom - view.zoom) < 1e-6 &&
    Math.abs(effective.center[1] - view.center[1]) < 1e-6
  )
    return camera;
  return {
    ...camera,
    center: effective.center,
    scaleDenominator:
      scaleDenominatorForZoom(effective.zoom, effective.center[1], pixelsPerMm) ??
      camera.scaleDenominator,
  };
}

/**
 * Converts a renderer event back to authored camera state. A move preserves physical scale;
 * an intentional renderer zoom establishes a new scale at the resulting centre latitude.
 */
export function compositionCameraFromMapView(
  current: CompositionCamera,
  view: MapView,
  pixelsPerMm: number,
  kind: CompositionCameraChangeKind,
): CompositionCamera | null {
  const scaleDenominator =
    kind === 'zoom'
      ? scaleDenominatorForZoom(view.zoom, view.center[1], pixelsPerMm)
      : current.scaleDenominator;
  if (scaleDenominator === null) return null;
  const supportedScaleDenominator = Math.min(
    COMPOSITION_SCALE_DENOMINATOR_MAX,
    Math.max(COMPOSITION_SCALE_DENOMINATOR_MIN, scaleDenominator),
  );
  return {
    center: [...view.center],
    scaleDenominator: supportedScaleDenominator,
    bearing: view.bearing,
    pitch: view.pitch,
  };
}

export function groundDistanceMetresForPhysicalLength(
  scaleDenominator: number,
  physicalLengthMm: number,
): number | null {
  if (!isScaleDenominatorSupported(scaleDenominator) || !isPositiveFinite(physicalLengthMm)) {
    return null;
  }
  return (scaleDenominator * physicalLengthMm) / 1000;
}

export function physicalLengthMmForGroundDistance(
  scaleDenominator: number,
  groundDistanceMetres: number,
): number | null {
  if (!isScaleDenominatorSupported(scaleDenominator) || !isPositiveFinite(groundDistanceMetres)) {
    return null;
  }
  return (groundDistanceMetres * 1000) / scaleDenominator;
}

export function formatScaleDenominator(scaleDenominator: number): string {
  return Math.round(scaleDenominator).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export function parseScaleDenominator(text: string): number | null {
  const compact = text.trim().replace(/[\s,_]/g, '');
  if (!/^\d+(?:\.\d+)?$/.test(compact)) return null;
  const value = Number(compact);
  return isScaleDenominatorSupported(value) ? value : null;
}

export function hasExactCompositionScale(pitch: number): boolean {
  return Number.isFinite(pitch) && Math.abs(pitch) <= COMPOSITION_EXACT_SCALE_PITCH_EPSILON;
}
